// Typed client for the conversational presentation-PLAN endpoints. Unlike the
// one-shot slide deck (`api/presentation.ts`), a plan is co-created with Eddi over
// an adaptive conversation and finalized into a detailed, time-boxed delivery plan
// (sections with markdown bodies + figures + Q&A + tips). Mirrors the backend
// `app/services/presentation_planner.py` shapes.
import { api } from "./client";

export type PresentationPlanStatus =
  | "planning"
  | "finalizing"
  | "complete"
  | "failed";

export const PLAN_IN_FLIGHT: PresentationPlanStatus[] = ["finalizing"];

export interface PlanMaterialRequest {
  title: string;
  why?: string;
  kind?: string;
}

export interface PresentationPlanTurn {
  id: string;
  role: "user" | "assistant";
  text: string;
  at: string;
  proposed_focus_areas?: string[];
  questions?: string[];
  material_requests?: PlanMaterialRequest[];
  ready_to_finalize?: boolean;
}

export interface PlanFigure {
  key: string;
  caption?: string;
  show_inline?: boolean;
  url?: string | null; // minted server-side on read
}

export interface PlanQA {
  question: string;
  answer?: string;
}

export interface PlanSection {
  order: number;
  title: string;
  minutes: number;
  figures?: PlanFigure[];
  body_markdown?: string;
}

export interface PresentationPlanJson {
  title: string;
  total_minutes: number;
  sections: PlanSection[];
  overall_delivery_tips?: string[];
  anticipated_qa?: PlanQA[];
}

export interface PresentationPlanRead {
  id: string;
  project_id: string;
  status: PresentationPlanStatus;
  duration_minutes: number;
  language: string;
  focus_areas: string[];
  conversation: PresentationPlanTurn[];
  draft_outline: { title?: string; gist?: string }[];
  plan_json: PresentationPlanJson | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

const enc = encodeURIComponent;

export const startPresentationPlan = (
  id: string,
  body: { duration_minutes: number; focus?: string; language?: string },
) => api.post<PresentationPlanRead>(`/projects/${enc(id)}/presentation-plan`, body);

export const getPresentationPlan = (id: string) =>
  api.get<PresentationPlanRead>(`/projects/${enc(id)}/presentation-plan`);

export const respondPresentationPlan = (
  id: string,
  body: {
    message?: string;
    selected_focus_areas?: string[];
    finish_now?: boolean;
  },
) => api.post<PresentationPlanRead>(`/projects/${enc(id)}/presentation-plan/respond`, body);

export const finalizePresentationPlan = (id: string, selectedFocusAreas?: string[]) =>
  api.post<PresentationPlanRead>(`/projects/${enc(id)}/presentation-plan/finalize`, {
    ...(selectedFocusAreas ? { selected_focus_areas: selectedFocusAreas } : {}),
  });

export const savePresentationPlan = (id: string, planJson: PresentationPlanJson) =>
  api.patch<PresentationPlanRead>(`/projects/${enc(id)}/presentation-plan`, {
    plan_json: planJson,
  });

export const adjustPresentationPlanSection = (
  id: string,
  sectionIndex: number,
  body: { directive: string; language?: string },
) =>
  api.post<PresentationPlanRead>(
    `/projects/${enc(id)}/presentation-plan/sections/${sectionIndex}/adjust`,
    body,
  );
